import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import { Table } from 'reactstrap';
import { D3LinePlot } from 'cd-eft/components/common/D3LinePlot/D3LinePlot';
import stores from 'cd-eft/models/Stores';
import { SOSurveyActions } from '../SOSurveyActions';
import { SOSpreadsheetVM } from './SOSpreadsheetVM';
import './SOSpreadsheet.scss';
import { ButtonToPlaceRow } from '../SOSurveys';
import { useEffect } from 'react';
import { Accordion } from 'cd-eft/components/common/Accordion';

const setStore = stores.AppStore.surveyEngineeringStore;
const designStore = stores.AppStore.designStore;

const SOSpreadsheetComponent = (props: any) => {
  const vm = useRef(new SOSpreadsheetVM(props)).current;

  const firstOffsetStation = setStore.stationOffsetObject.offsetStations[0];

  useEffect(() => {
    if (firstOffsetStation) {
      vm.refillPlotData(firstOffsetStation);
    }
  }, []);

  return (
    <>
      <Accordion headers={['Profile']} expanded={true} >
        <div className="so-graph-container">
          <D3LinePlot targetDivID={vm.targetDivID} windowOptions={vm.windowOptions} plotData={vm.plotData} />
        </div>
      </Accordion>

      <form className="usa-form usa-form--large form-with-fieldset">
        <fieldset className="usa-fieldset border-0 p-2">
          <div className="form-row-fixed-height mt-3">
            <label className="grid-col-3 usa-label" htmlFor="alignmentName">
              Alignment Name
            </label>
            <input
              className="grid-col-6 usa-input"
              name="alignmentName"
              aria-label="alignmentName"
              data-testid="alignmentName"
              placeholder={setStore.stationOffsetObject.alignmentName}
              value={setStore.stationOffsetObject.alignmentName}
              type="text"
              disabled
            />
          </div>
          <div className="form-row-fixed-height mt-3">
            <label className="grid-col-3 usa-label" htmlFor="offset">
              Station Step (ft)
            </label>
            <div className="grid-col-2">
              <input
                className="usa-input"
                data-testid="stationStep"
                name="stationStep"
                aria-label="stationStep"
                type="number"
                min={0}
                value={firstOffsetStation.stationStep}
                onChange={vm.updateAllOffsetStations}
              />
            </div>
            <label className="grid-col-3 usa-label flex-justify-end">Entry Mode</label>
            <div className="grid-col-auto usa-radio">
              <input
                className="usa-radio__input"
                data-testid="elevation"
                type="radio"
                id="elevation"
                name="elevation"
                aria-label="elevation"
                value="ELEVATION"
                checked={firstOffsetStation.entryMode === 'ELEVATION'}
                onChange={vm.setEntryMode}
                disabled={setStore.stationOffsetObject.surveyId !== '' && firstOffsetStation.entryMode !== 'ELEVATION'}
              />
              <label className="usa-radio__label" htmlFor="elevation">
                Elevation
              </label>
            </div>
            <div className="grid-col-auto usa-radio">
              <input
                className="usa-radio__input"
                data-testid="rod"
                type="radio"
                id="rod"
                name="rod"
                aria-label="rod"
                value="ROD"
                checked={firstOffsetStation.entryMode === 'ROD'}
                onChange={vm.setEntryMode}
                disabled={setStore.stationOffsetObject.surveyId !== '' && firstOffsetStation.entryMode !== 'ROD'}
              />
              <label className="usa-radio__label" htmlFor="rod">
                Rod
              </label>
            </div>
          </div>
        </fieldset>

        <Accordion headers={['Table']} expanded={true} >
          <div className="set-table-container height-auto">
            <Table className="set-table">
              <thead>
                <tr>
                  <th className="pr-0">Select</th>
                  <th>Station</th>
                  {firstOffsetStation.entryMode === 'ELEVATION' ? null : <th>HI</th>}
                  <th>-50</th>
                  <th>0</th>
                  <th>50</th>
                </tr>
              </thead>
              <tbody>
                {setStore.stationOffsetObject.offsetStations.map((offsetStation, i) => (
                  <tr key={`key-offsetStation-${i}`}>
                    <td className="text-center p-0">
                      <input
                        id={'select' + i}
                        name="select"
                        aria-label={'select' + i}
                        data-testid={'select' + i}
                        type="checkbox"
                        value={i}
                        checked={vm.checkboxes.includes(i)}
                        onChange={vm.toggleCheckbox}
                      />
                      <ButtonToPlaceRow i={i} addRow={vm.addRow} />
                    </td>
                    <td width={firstOffsetStation.entryMode === 'ELEVATION' ? '25%' : '20%'}>
                      <input
                        className="usa-input"
                        name="stationDistance"
                        aria-label={`stationDistance${i}`}
                        data-testid={`stationDistance${i}`}
                        data-i={i}
                        type="number"
                        placeholder="0.00"
                        min={0}
                        max={100}
                        onChange={vm.updateSingleOffsetStation}
                        value={offsetStation.stationDistance}
                        onFocus={vm.onFocusHandler}
                        onBlur={vm.onBlurHandler}
                      />
                    </td>
                    {firstOffsetStation.entryMode === 'ROD' && (
                      <td width="20%">
                        <input
                          className="usa-input"
                          name="hiElevation"
                          aria-label={`hiElevation${i}`}
                          data-testid={`hiElevation${i}`}
                          data-i={i}
                          type="number"
                          placeholder="0.00"
                          min={0}
                          value={offsetStation.hiElevation}
                          onChange={vm.updateSingleOffsetStation}
                          onFocus={vm.onFocusHandler}
                          onBlur={vm.onBlurHandler}
                        />
                      </td>
                    )}
                    {offsetStation.offsets.map((offset, j) => (
                      <td width={firstOffsetStation.entryMode === 'ELEVATION' ? '25%' : '20%'} key={`offset-${i}-${j}`}>
                        {/* negative or positive if ROD selected */}
                        <input
                          className="usa-input"
                          name="z"
                          aria-label={`offset-${i}-${j}`}
                          data-testid={`offset-${i}-${j}`}
                          data-i={i}
                          data-j={j}
                          type="number"
                          placeholder="0.00"
                          onChange={vm.updateSingleOffset}
                          min={firstOffsetStation.entryMode === 'ROD' ? -10000 : 0}
                          value={offset.z}
                          onFocus={vm.onFocusHandler}
                          onBlur={vm.onBlurHandler}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="container-for-last-element">
              <ButtonToPlaceRow i={setStore.stationOffsetObject.offsetStations.length} addRow={vm.addRow} />
            </div>
          </div>
        </Accordion>

        <SOSurveyActions add={vm.addOffsetStationHandler} deleteStation={vm.deleteOffsetStationHandler} />
      </form>
    </>
  );
};

export const SOSpreadsheet = observer(SOSpreadsheetComponent);
